import { digestInChunks } from './digest'

/**
 * sha256 of a File for early duplicate detection (optional in the contract).
 *
 * `crypto.subtle.digest` is one-shot, so the file is read in chunks (progress)
 * into one buffer and digested natively. That runs in a Web Worker to keep the UI
 * smooth; without workers only small files are hashed on the main thread. Large
 * files (> 256 MiB, enforced by the manager) and insecure contexts (no
 * `crypto.subtle`) are skipped — the server still dedupes after upload.
 */

const MAIN_THREAD_MAX_BYTES = 32 * 1024 * 1024

function subtleAvailable(): boolean {
  return typeof crypto !== 'undefined' && !!crypto.subtle && typeof crypto.subtle.digest === 'function'
}

let workerSupported: boolean | null = null

function hashInWorker(file: File, onProgress: (ratio: number) => void, signal: AbortSignal): Promise<string | null> {
  return new Promise((resolve, reject) => {
    let worker: Worker
    try {
      worker = new Worker(new URL('./hash.worker.ts', import.meta.url), { type: 'module' })
    } catch {
      workerSupported = false
      resolve(null)
      return
    }
    const done = () => {
      signal.removeEventListener('abort', onAbort)
      worker.terminate()
    }
    const onAbort = () => {
      done()
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
    worker.onmessage = (e: MessageEvent<{ type: 'progress'; ratio: number } | { type: 'done'; hex: string } | { type: 'error'; message: string }>) => {
      const msg = e.data
      if (msg.type === 'progress') onProgress(msg.ratio)
      else if (msg.type === 'done') {
        done()
        resolve(msg.hex)
      } else {
        done()
        resolve(null)
      }
    }
    worker.onerror = () => {
      done()
      workerSupported = false
      resolve(null)
    }
    worker.postMessage({ file })
  })
}

export async function hashFile(file: File, onProgress: (ratio: number) => void, signal: AbortSignal): Promise<string | null> {
  if (!subtleAvailable()) return null
  if (typeof Worker !== 'undefined' && workerSupported !== false) {
    const hex = await hashInWorker(file, onProgress, signal)
    // `workerSupported` may have flipped to false inside the call.
    if (hex || (workerSupported as boolean | null) !== false) return hex
  }
  if (file.size > MAIN_THREAD_MAX_BYTES) return null
  return digestInChunks(file, onProgress, () => signal.aborted)
}
